import type { EnvironmentProviders, Provider } from '@angular/core';
import { inject, Injector, isDevMode } from '@angular/core';
import { environment } from '@env/environment';
import type { SentryLibraryConfiguration } from '@kerberos-compliance/compliance-shared-ui';
import { provideErrorHandler, SentryLibraryConfigurationToken } from '@kerberos-compliance/compliance-shared-ui';
import { browserTracingIntegration } from '@sentry/angular';
import { AuthStore } from '@/auth/store/auth.store';

const errorHandlerConfig: SentryLibraryConfiguration = {
  dsn: environment.config.sentry_dsn,
  // Sentry is off in local dev by default; the `sentry` debug flag force-enables delivery for testing.
  // Gate on productionBuild (not isDevMode()): deployed builds like preview run with optimization
  // disabled, so isDevMode() is true there and would wrongly suppress delivery.
  enableSentry: environment.productionBuild || environment.debugFlags.includes('sentry'),
  integrations: [browserTracingIntegration()],
  tracesSampleRate: 1,
  release: environment.version,
  environment: environment.target,
  enableConsoleLogging: isDevMode(),
};

/**
 * Configures the shared-ui Sentry error handler and enriches reported events with the current user.
 *
 * The `getUserId` hook is bound through a factory that resolves {@link AuthStore} lazily via the injector
 */
export const errorHandlerProviders: (Provider | EnvironmentProviders)[] = [
  provideErrorHandler(errorHandlerConfig),
  {
    provide: SentryLibraryConfigurationToken,
    useFactory: (): SentryLibraryConfiguration => {
      const injector = inject(Injector);
      return { ...errorHandlerConfig, getUserId: () => injector.get(AuthStore).currentUserId() };
    },
  },
];
